import React, { useMemo, useState, useEffect } from 'react';
import { Editor } from '@tiptap/react';
import { Button } from '@os/components/ui/button';
import { useToast } from '@os/components/ui/use-toast';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { useUserSettings } from '@/OS/hooks/useUserSettings';
import { sendMessageToModel } from '@/services/ModelService';
import { AIModel, Message } from '@/types';
import { generateId } from '@/lib/utils';
import { Sparkles, Wand2, AlignJustify, FileText, CheckCircle2, Blocks, X, GripHorizontal } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@os/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@os/components/ui/dropdown-menu';
import { Input } from '@os/components/ui/input';
import { mdToHtml } from '@/OS/utils/markdown';
import { useLocalLLM } from '@/contexts/LocalLLMContext';
import { useAppStore } from '@/store';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

type Props = {
  editor: Editor | null;
  // Preview-only configuration (optional)
  preview?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
  // Review mode integration (optional)
  isAIStreamingRef?: React.MutableRefObject<boolean>;
  aiStreamStartPosRef?: React.MutableRefObject<number | null>;
  onAIStreamComplete?: () => void;
};

const MapleWordAiMenu: React.FC<Props> = ({
  editor,
  preview = false,
  containerRef,
  isAIStreamingRef,
  aiStreamStartPosRef,
  onAIStreamComplete
}) => {
  const { toast } = useToast();
  const { models, fetchModels } = useSupabaseStore();
  const { settings: osAISettings } = useUserSettings('mapleos-ai', { defaultModelId: 'maplept-free' });
  const { isAvailable: isLocalLLMAvailable, generate: generateLocal } = useLocalLLM();
  const { user: appUser } = useAppStore();
  const { user } = useAuth();

  useEffect(() => {
    if (!models || models.length === 0) {
      fetchModels().catch(() => {});
    }
  }, [models, fetchModels]);

  const [loading, setLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(preview); // OPEN by default in preview
  const [prompt, setPrompt] = useState('');
  const [profileDefaultModelApiName, setProfileDefaultModelApiName] = useState<string | null>(null);
  const streamingPositionRef = React.useRef<number | null>(null); // Track where streaming text starts
  const accumulatedTextRef = React.useRef<string>(''); // Accumulate markdown text during streaming

  useEffect(() => {
    let cancelled = false;

    const loadProfileDefaultModel = async () => {
      if (!user?.id) {
        setProfileDefaultModelApiName(null);
        return;
      }

      const { data } = await supabase
        .from('profiles')
        .select('default_model')
        .eq('id', user.id)
        .maybeSingle();

      if (!cancelled) {
        const profileData = data as { default_model?: string | null } | null;
        const defaultModel = profileData && typeof profileData.default_model === 'string' ? profileData.default_model : null;
        setProfileDefaultModelApiName(defaultModel);
      }

    };

    loadProfileDefaultModel();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Dragging state
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = React.useRef<{ x: number, y: number } | null>(null);
  const initialOffsetRef = React.useRef<{ x: number, y: number } | null>(null);
  const toolbarRef = React.useRef<HTMLDivElement | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Disable dragging entirely in preview for stability
    if (preview) {
      return;
    }
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    initialOffsetRef.current = { ...offset };
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !dragStartRef.current || !initialOffsetRef.current) return;
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;

      setOffset({
        x: initialOffsetRef.current.x + dx,
        y: initialOffsetRef.current.y + dy
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging && !preview) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, preview]);

  // Clamp offset to container bounds ONLY when not in preview
  useEffect(() => {
    if (preview) return;
    if (!containerRef?.current || !toolbarRef.current) return;
    const cRect = containerRef.current.getBoundingClientRect();
    const tRect = toolbarRef.current.getBoundingClientRect();
    const pad = 8;

    const nextX = Math.min(Math.max(offset.x, -cRect.width / 2 + pad), cRect.width / 2 - pad);
    const maxY = Math.max(0, cRect.height - tRect.height - pad);
    const nextY = Math.min(maxY, Math.max(0, offset.y));

    if (nextX !== offset.x || nextY !== offset.y) {
      setOffset({ x: nextX, y: nextY });
    }
  }, [offset, preview, containerRef]);

  const model: AIModel | null = useMemo(() => {
    const list = Array.isArray(models) ? models : [];
    const activeModels = list.filter((item) => item.is_active !== false);

    const preferredApiName = appUser?.defaultModelApiName || profileDefaultModelApiName;
    if (preferredApiName) {
      const preferredByApiName = activeModels.find((item) => item.apiName === preferredApiName);
      if (preferredByApiName) {
        return preferredByApiName;
      }
    }

    const preferredId = (osAISettings as any)?.defaultModelId as string | undefined;
    if (preferredId) {
      const preferredById = activeModels.find((item) => item.id === preferredId);
      if (preferredById) {
        return preferredById;
      }
    }

    return activeModels.find((item) => item.tier === 'free') || activeModels[0] || null;
  }, [appUser, models, osAISettings, profileDefaultModelApiName]);

  if (!editor) return null;

  // NEW: track last selection snapshot so clicks don't lose selection
  const lastSelectionRef = React.useRef<{ from: number; to: number } | null>(null);
  const snapshotSelection = () => {
    const sel = editor.state.selection;
    lastSelectionRef.current = { from: sel.from, to: sel.to };
  };

  const getSelectedText = (): string => {
    const snap = lastSelectionRef.current;
    if (snap && snap.to > snap.from) {
      return editor.state.doc.textBetween(snap.from, snap.to, '\n') || '';
    }
    const { from, to } = editor.state.selection;
    return editor.state.doc.textBetween(from, to, '\n') || '';
  };

  // NEW: replace only the selected range with provided HTML using snapshot
  const replaceSelectionWithHtml = (html: string) => {
    const snap = lastSelectionRef.current;
    if (snap && snap.to > snap.from) {
      editor.chain().focus().setTextSelection({ from: snap.from, to: snap.to }).deleteSelection().insertContent(html).run();
    } else {
      const { from, to } = editor.state.selection;
      if (to > from) {
        editor.chain().focus().setTextSelection({ from, to }).deleteSelection().insertContent(html).run();
      } else {
        // if no selection, insert at cursor
        editor.chain().focus().insertContent(html).run();
      }
    }
  };

  const insertAtCursor = (text: string) => {
    editor.chain().focus().insertContent(text).run();
  };

  // NEW: fallback to full-document change via event (lets Review Mode stage the change)
  const dispatchFullDocReplace = (textOrHtml: string) => {
    window.dispatchEvent(new CustomEvent('mapleos-mapleword-insert', {
      detail: {
        text: textOrHtml, // MapleWord will convert text → HTML as needed
        replace: true,
      }
    }));
  };

  const beginAIStream = () => {
    if (isAIStreamingRef) {
      isAIStreamingRef.current = true;
      if (aiStreamStartPosRef) {
        aiStreamStartPosRef.current = editor.getText().length;
      }
    }
  };

  const resetAIStream = () => {
    if (isAIStreamingRef) isAIStreamingRef.current = false;
    if (aiStreamStartPosRef) aiStreamStartPosRef.current = null;
    streamingPositionRef.current = null;
    accumulatedTextRef.current = '';
  };

  const finalizeGeneratedContent = async (insertPos: number, text: string) => {
    if (!text.trim()) {
      toast({ title: 'No result', description: 'The model returned an empty response.', variant: 'destructive' });
      resetAIStream();
      return false;
    }

    const htmlContent = await mdToHtml(text);
    editor.chain().focus().insertContentAt(insertPos, htmlContent).run();
    onAIStreamComplete?.();
    streamingPositionRef.current = null;
    accumulatedTextRef.current = '';
    return true;
  };

  const runPrompt = async (promptText: string, insertPos: number, onSuccess?: () => void) => {
    if (!model) {
      toast({ title: 'No AI model available', description: 'Please select an available model first.', variant: 'destructive' });
      setLoading(false);
      return;
    }

    beginAIStream();

    try {
      if (model.provider === 'local') {
        if (!isLocalLLMAvailable) {
          toast({ title: 'Local AI not ready', description: 'Please wait for the local AI model to load, or choose a different model.', variant: 'destructive' });
          setLoading(false);
          resetAIStream();
          return;
        }

        await generateLocal({
          prompt: promptText,
          maxTokens: 4096,
          temperature: 0.7,
          onToken: (token) => {
            accumulatedTextRef.current += token;
            if (streamingPositionRef.current !== null) {
              editor.chain().focus().insertContent(token).run();
              streamingPositionRef.current += token.length;
            }
          },
          onDone: async (fullText) => {
            setLoading(false);

            if (!fullText.trim()) {
              toast({ title: 'No result', description: 'The model returned an empty response.', variant: 'destructive' });
              resetAIStream();
              return;
            }

            const htmlContent = await mdToHtml(accumulatedTextRef.current);
            const endPos = streamingPositionRef.current || editor.state.doc.content.size;
            editor.chain()
              .focus()
              .setTextSelection({ from: insertPos, to: endPos })
              .deleteSelection()
              .insertContentAt(insertPos, htmlContent)
              .run();

            streamingPositionRef.current = null;
            accumulatedTextRef.current = '';
            onAIStreamComplete?.();
            onSuccess?.();
            toast({ title: 'AI complete', description: 'Generated response.' });
          },
        });
        return;
      }

      const message: Message = {
        id: generateId(),
        chatId: 'mapleword',
        content: promptText,
        role: 'user',
        sender: 'user',
        timestamp: new Date().toISOString(),
      };

      const response = await sendMessageToModel([message], model, Array.isArray(models) ? models : []);
      setLoading(false);

      const completed = await finalizeGeneratedContent(insertPos, response?.content || '');
      if (!completed) {
        return;
      }

      onSuccess?.();
      toast({ title: 'AI complete', description: `Generated with ${model.name}.` });
    } catch (e: any) {
      toast({ title: 'AI error', description: e?.message || 'Failed to run AI action.', variant: 'destructive' });
      setLoading(false);
      resetAIStream();
    }
  };

  const runQuick = async (kind: 'rewrite' | 'summarize' | 'grammar' | 'continue' | 'tone_formal' | 'tone_casual') => {
    const selection = getSelectedText();
    const hasSelection = selection.trim().length > 0;

    setLoading(true);

    const fullDocText = editor.state.doc.textBetween(0, editor.state.doc.content.size, '\n');

    const prompts: Record<string, string> = {
      rewrite: `Rewrite the following ${hasSelection ? 'selection' : 'document'} to improve clarity, style, and flow. Expand on ideas where appropriate and add more detail to make it more comprehensive. Maintain the original meaning but feel free to elaborate.\n\n---\n${hasSelection ? selection : fullDocText}\n---`,
      summarize: `Summarize the following ${hasSelection ? 'selection' : 'document'} in 3-5 concise bullet points. Return only the bullet list.\n\n${hasSelection ? selection : fullDocText}`,
      grammar: `Fix grammar and spelling in the following ${hasSelection ? 'selection' : 'document'}. Also improve sentence structure and readability where needed.\n\n${hasSelection ? selection : fullDocText}`,
      continue: `Continue the following text naturally. Write several more paragraphs (at least 3-5 sentences) that expand on the topic and keep the tone consistent. Be detailed and thorough.\n\n${hasSelection ? selection : editor.state.doc.textBetween(0, editor.state.selection.to, '\n')}`,
      tone_formal: `Rewrite the following ${hasSelection ? 'selection' : 'document'} in a more formal, professional tone. Expand where appropriate to make it more detailed and comprehensive.\n\n${hasSelection ? selection : fullDocText}`,
      tone_casual: `Rewrite the following ${hasSelection ? 'selection' : 'document'} in a more casual, friendly, conversational tone. Feel free to add personality and expand on ideas.\n\n${hasSelection ? selection : fullDocText}`,
    };

    const snap = lastSelectionRef.current;
    let insertPos: number;

    if (kind === 'continue') {
      insertPos = editor.state.selection.to;
    } else if (hasSelection && snap && snap.to > snap.from) {
      editor.chain().focus().setTextSelection({ from: snap.from, to: snap.to }).deleteSelection().run();
      insertPos = snap.from;
    } else if (!hasSelection) {
      editor.chain().focus().setContent('').run();
      insertPos = 0;
    } else {
      insertPos = editor.state.selection.to;
    }

    streamingPositionRef.current = insertPos;
    accumulatedTextRef.current = '';

    await runPrompt(prompts[kind], insertPos);
  };

  const runCustom = async () => {
    if (!prompt.trim()) return;

    setLoading(true);

    const selection = getSelectedText();
    const hasSelection = selection.trim().length > 0;
    const fullDocText = editor.state.doc.textBetween(0, editor.state.doc.content.size, '\n');

    const content = hasSelection
      ? `You are assisting writing. Here is the current selection:\n---\n${selection}\n---\n\nUser request: ${prompt}`
      : `You are assisting writing. Here is the current document:\n---\n${fullDocText}\n---\n\nUser request: ${prompt}`;

    const snap = lastSelectionRef.current;
    let insertPos: number;

    if (hasSelection && snap && snap.to > snap.from) {
      editor.chain().focus().setTextSelection({ from: snap.from, to: snap.to }).deleteSelection().run();
      insertPos = snap.from;
    } else if (!hasSelection) {
      editor.chain().focus().setContent('').run();
      insertPos = 0;
    } else {
      insertPos = editor.state.selection.to;
    }

    streamingPositionRef.current = insertPos;
    accumulatedTextRef.current = '';

    await runPrompt(content, insertPos, () => {
      setPrompt('');
      setComposerOpen(false);
    });
  };

  return (
    <>
      {/* Bottom-centered AI dock */}
      <div
        ref={toolbarRef}
        className={`pointer-events-none absolute ${preview ? "left-4 right-4 bottom-4 z-[100]" : "left-1/2 bottom-6 z-[2000000]"}`}
        style={{
          transform: preview ? undefined : `translate(calc(-50% + ${offset.x}px), ${offset.y}px)`,
          maxWidth: preview ? 'calc(100% - 1rem)' : undefined,
        }}
      >
        <TooltipProvider>
          <div
            className="pointer-events-auto bg-background/80 backdrop-blur-sm border shadow-lg rounded-2xl px-2 py-2 flex items-center max-w-full"
            contentEditable={false}
            onMouseDown={(e) => { e.stopPropagation(); snapshotSelection(); }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag Handle (hidden in preview for stability) */}
            {!preview && (
              <div
                className="cursor-move p-1.5 mr-1 text-muted-foreground hover:text-foreground transition-colors hover:bg-muted/50 rounded-md"
                onMouseDown={(e) => { snapshotSelection(); handleMouseDown(e); }}
              >
                <GripHorizontal className="h-4 w-4" />
              </div>
            )}

            <div className="flex flex-col md:flex-row md:items-center md:flex-nowrap gap-2 w-full">
              {/* Actions row */}
              <div className="flex flex-wrap md:flex-nowrap items-center justify-center md:justify-start gap-1 md:gap-2 shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button disabled={loading} variant="ghost" size="sm" onMouseDown={snapshotSelection} onClick={() => runQuick('rewrite')}>
                      <Wand2 className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Rewrite</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Rewrite</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button disabled={loading} variant="ghost" size="sm" onMouseDown={snapshotSelection} onClick={() => runQuick('continue')}>
                      <AlignJustify className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Continue</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Continue</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button disabled={loading} variant="ghost" size="sm" onMouseDown={snapshotSelection} onClick={() => runQuick('summarize')}>
                      <FileText className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Summarize</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Summarize</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button disabled={loading} variant="ghost" size="sm" onMouseDown={snapshotSelection} onClick={() => runQuick('grammar')}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Grammar</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Grammar</TooltipContent>
                </Tooltip>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button disabled={loading} variant="outline" size="sm" onMouseDown={snapshotSelection}>
                      <Blocks className="h-4 w-4 mr-1" /> Tone
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-32">
                    <DropdownMenuItem onMouseDown={snapshotSelection} onClick={() => runQuick('tone_formal')}>Formal</DropdownMenuItem>
                    <DropdownMenuItem onMouseDown={snapshotSelection} onClick={() => runQuick('tone_casual')}>Casual</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <div className="mx-1 h-5 w-px bg-border hidden md:block" />

                {!composerOpen && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="rounded-full"
                    onMouseDown={snapshotSelection}
                    onClick={() => setComposerOpen(true)}
                    title="Ask AI"
                  >
                    <Sparkles className="h-4 w-4 mr-1" /> Ask AI
                  </Button>
                )}
              </div>

              {composerOpen && (
                <div className="flex items-center gap-2 w-full md:flex-1 md:min-w-[320px]">
                  <Sparkles className="h-4 w-4 text-muted-foreground" />
                  <Input
                    className="h-9 w-full md:w-auto md:flex-1 min-w-0"
                    placeholder="Tell AI what to do…"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onMouseDown={(e) => { e.stopPropagation(); snapshotSelection(); }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runCustom(); } }}
                  />
                  <Button disabled={loading || !prompt.trim()} size="sm" onMouseDown={snapshotSelection} onClick={runCustom}>
                    {loading ? 'Running…' : 'Run'}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setComposerOpen(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </TooltipProvider>
      </div>
    </>
  );
};

export default MapleWordAiMenu;